<template>
  <div class="search-summary" :class="{ desktop: $isDesktop }">
    <span class="search-heading">
      <slot name="search-summary-heading" />

      <pu-search-wizard-link
        v-if="query && !$isDesktop"
        class="search-term-link"
      >
        <translate :tt="{ query }"> Showing results for {query} </translate>
      </pu-search-wizard-link>
      <a
        v-if="query && $isDesktop"
        class="search-term-link desktop"
        @click="onDesktopQueryLinkClick"
      >
        <translate :tt="{ query }"> Showing results for {query} </translate>
      </a>
      <p>
        <search-summary-availability-search
          v-if="isAvailabilitySearch"
          :count="count"
          :category-name="categoryName"
          :hierarchy-name="hierarchyName"
          :distance="distance"
          :search-place="searchPlace"
          :is-user-location-search="isUserLocationSearch"
        />
        <search-summary-bookable-count-search
          v-else-if="bookableCount"
          :bookable-count="bookableCount"
          :category-ids="filters.categoryIds"
          :category-name="categoryName"
          :distance="distance"
          :hierarchy-name="hierarchyName"
          :is-user-location-search="isUserLocationSearch"
          :search-place="searchPlace"
        />
        <search-summary-listing
          v-else-if="bookableCount === 0"
          :bookable-count="bookableCount"
          :count="campsitesCount"
          :distance="distance"
          :hierarchy-name="hierarchyName"
          :is-user-location-search="isUserLocationSearch"
          :search-place="searchPlace"
        />
        <span v-if="!$isDesktop && campsitesCount > 0" class="sort-summary">
          <translate
            v-if="sortOption === 'distance'"
            data-test="search-results-sort"
            data-nosnippet
            >Sorted by distance (as the crow flies)</translate
          >
          <translate
            v-else
            :key="`sorted-by-${sortOption}`"
            data-test="search-results-sort"
            data-nosnippet
            >Sorted by {{ sortOption }}</translate
          >
        </span>
      </p>
    </span>
    <sort-select v-if="$isDesktop && campsitesCount > 0" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { routeToFilters } from '../../../pu-links/infrastructure/routeToFilters'

@Component({
  setup() {
    const searchWizardStore = useSearchWizardStore(usePinia())
    return { searchWizardStore }
  },
})
class SearchSummaryMain extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>

  @Prop()
  isAvailabilitySearch: boolean

  @Prop()
  categoryName?: string

  @Prop()
  hierarchyName: string

  @Prop({ default: false })
  isUserLocationSearch: boolean

  @Prop()
  distance: { mi: number; km: number }

  @Prop()
  bookableCount: number

  @Prop()
  campsitesCount: number

  @Prop()
  count: number

  @Prop({ required: false })
  query?: string

  @Prop({ required: false })
  placename?: string

  @Prop()
  sortOption?: string

  get searchPlace(): string | undefined {
    return this.query || this.placename || this.hierarchyName
  }

  get filters() {
    return routeToFilters(this.$route)
  }

  onDesktopQueryLinkClick() {
    this.searchWizardStore.isSearchWizardOpen = true
    this.searchWizardStore.send('FOCUS_LOCATION')
  }
}

export default toNative(SearchSummaryMain)
</script>

<style scoped>
.search-summary {
  background-color: var(--colour-page-background);
  color: var(--colour-wcag-blue);
  padding: 0.4em 4%;
  width: 100vw;
}

:deep(h1) {
  font-size: 1.25em;
  font-weight: bold;
  line-height: 1.15;
  margin: 0 0 0.3em;
  text-align: center;
}

p {
  align-items: flex-end;
  display: flex;
  font-size: var(--small);
  gap: 2em;
  justify-content: space-between;
  line-height: 1.1;
}

p span:last-of-type {
  text-align: right;
}

.search-term-link {
  color: var(--colour-wcag-blue);
  display: block;
  font-size: var(--body);
  margin: 0 0 0.3em 0;
  max-width: 100vw;
  overflow-wrap: break-word;
  text-align: center;
}

.search-term-link.desktop {
  text-align: unset;
}

.sort-summary {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 15ch;
}

/* desktop & tablet */
.search-summary.desktop {
  align-items: flex-start;
  display: flex;
  flex-shrink: 1;
  gap: 2em;
  justify-content: space-between;
  background: var(--colour-page-background-light);
  margin: 0;
  padding: 0.5em var(--page-width-padding) 0;
  width: auto;
}

.desktop p {
  font-size: var(--body);
}

.desktop :deep(h1) {
  margin: 0 0 0.25em;
  text-align: left;
}

.desktop p span:last-of-type {
  text-align: left;
}

.desktop .search-heading {
  display: block;
  /* don't push sort select out of container */
  max-width: calc(100% - 20em);
}
</style>
